"use client";

/**
 * Estado global del lado cliente de la app (rediseño 2026).
 *
 * - carrito y favoritos: viven en localStorage (funcionan sin cuenta).
 * - usuario: se hidrata desde GET /api/auth/me (cookie httpOnly).
 * - zona: la del usuario logueado, o la elegida en el onboarding (localStorage).
 *
 * Los PRECIOS mostrados en cliente son informativos: el servidor recalcula
 * todo al crear el pedido (POST /api/pedidos).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface UsuarioApp {
  id: string;
  nombre: string;
  telefono: string;
  email: string | null;
  zonaSlug: string | null;
  distCodigo: string | null;
  /** Últimos datos de facturación usados (para prellenar el checkout). */
  facturacion: { identificacion: string; nombre: string; email: string } | null;
}

interface EstadoApp {
  /** slug → cantidad. */
  carrito: Record<string, number>;
  /** slug → true. */
  favoritos: Record<string, boolean>;
  /** `undefined` = cargando; `null` = sin sesión. */
  usuario: UsuarioApp | null | undefined;
  /** Descuento vigente por ser distribuidor (0.18 = 18%; 0 = no aplica). */
  descuentoDist: number;
  /** Zona efectiva (usuario o elegida en onboarding). */
  zonaSlug: string | null;
  agregar: (slug: string, cantidad?: number) => void;
  setCantidad: (slug: string, cantidad: number) => void;
  vaciarCarrito: () => void;
  toggleFavorito: (slug: string) => void;
  setZona: (slug: string) => void;
  refrescarUsuario: () => Promise<void>;
  totalItems: number;
}

const Ctx = createContext<EstadoApp | null>(null);

function leerLS<T>(clave: string, porDefecto: T): T {
  if (typeof window === "undefined") return porDefecto;
  try {
    const crudo = window.localStorage.getItem(clave);
    return crudo ? (JSON.parse(crudo) as T) : porDefecto;
  } catch {
    return porDefecto;
  }
}

function guardarLS(clave: string, valor: unknown): void {
  try {
    window.localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    // Modo incógnito / cuota llena: el carrito vive solo en memoria.
  }
}

export function ProveedorApp({ children }: { children: ReactNode }) {
  const [carrito, setCarrito] = useState<Record<string, number>>({});
  const [favoritos, setFavoritos] = useState<Record<string, boolean>>({});
  const [usuario, setUsuario] = useState<UsuarioApp | null | undefined>(undefined);
  const [zonaLocal, setZonaLocal] = useState<string | null>(null);

  // Hidratación desde localStorage (solo cliente, tras montar).
  useEffect(() => {
    setCarrito(leerLS("fz_carrito", {}));
    setFavoritos(leerLS("fz_favoritos", {}));
    setZonaLocal(leerLS<string | null>("fz_zona", null));
  }, []);

  const refrescarUsuario = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/me", { cache: "no-store" });
      if (!res.ok) {
        setUsuario(null);
        return;
      }
      const data = (await res.json()) as { usuario: UsuarioApp | null };
      setUsuario(data.usuario);
    } catch {
      setUsuario(null);
    }
  }, []);

  useEffect(() => {
    void refrescarUsuario();
  }, [refrescarUsuario]);

  // Descuento de distribuidor: se resuelve del nivel vigente en el servidor.
  const [descuentoDist, setDescuentoDist] = useState(0);
  const distCodigo = usuario?.distCodigo ?? null;
  useEffect(() => {
    if (!distCodigo) {
      setDescuentoDist(0);
      return;
    }
    fetch("/api/distribuidores/mi")
      .then((r) => r.json())
      .then((data) =>
        setDescuentoDist(data.ok ? (data.portal.nivel?.descuento ?? 0) : 0),
      )
      .catch(() => setDescuentoDist(0));
  }, [distCodigo]);

  const agregar = useCallback((slug: string, cantidad = 1) => {
    setCarrito((c) => {
      const nuevo = { ...c, [slug]: (c[slug] ?? 0) + cantidad };
      guardarLS("fz_carrito", nuevo);
      return nuevo;
    });
  }, []);

  const setCantidad = useCallback((slug: string, cantidad: number) => {
    setCarrito((c) => {
      const nuevo = { ...c };
      if (cantidad <= 0) delete nuevo[slug];
      else nuevo[slug] = cantidad;
      guardarLS("fz_carrito", nuevo);
      return nuevo;
    });
  }, []);

  const vaciarCarrito = useCallback(() => {
    setCarrito({});
    guardarLS("fz_carrito", {});
  }, []);

  const toggleFavorito = useCallback((slug: string) => {
    setFavoritos((f) => {
      const nuevo = { ...f, [slug]: !f[slug] };
      if (!nuevo[slug]) delete nuevo[slug];
      guardarLS("fz_favoritos", nuevo);
      return nuevo;
    });
  }, []);

  const setZona = useCallback((slug: string) => {
    setZonaLocal(slug);
    guardarLS("fz_zona", slug);
  }, []);

  const totalItems = useMemo(
    () => Object.values(carrito).reduce((t, n) => t + n, 0),
    [carrito],
  );

  const valor = useMemo<EstadoApp>(
    () => ({
      carrito,
      favoritos,
      usuario,
      descuentoDist,
      zonaSlug: usuario?.zonaSlug ?? zonaLocal,
      agregar,
      setCantidad,
      vaciarCarrito,
      toggleFavorito,
      setZona,
      refrescarUsuario,
      totalItems,
    }),
    [
      carrito,
      favoritos,
      usuario,
      descuentoDist,
      zonaLocal,
      agregar,
      setCantidad,
      vaciarCarrito,
      toggleFavorito,
      setZona,
      refrescarUsuario,
      totalItems,
    ],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useApp(): EstadoApp {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useApp debe usarse dentro de <ProveedorApp>");
  return ctx;
}
